import { Box, Button, Field, Heading, Image, Input, Text, VStack } from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { useResetPasswordConfirm } from "../../hooks/useAuthQueries"
import { getAuthErrorMessage } from "../../utils/authErrors"

const ResetPasswordConfirm: React.FC = () => {
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [newPassword1, setNewPassword1] = useState("")
  const [newPassword2, setNewPassword2] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const mutation = useResetPasswordConfirm()

  const uid = params.uid || new URLSearchParams(location.search).get("uid") || ""
  const token = params.token || new URLSearchParams(location.search).get("token") || ""

  useEffect(() => {
    if (!uid || !token) {
      // If missing critical params, send user back to request reset
      navigate("/reset-password")
    }
  }, [uid, token, navigate])

  // A bad/expired uid or token means the link itself is dead — no amount of
  // retrying the form will fix that, so point the user back to request a new one.
  const isDeadLink = (error: any) =>
    Boolean(error?.response?.data?.uid || error?.response?.data?.token)

  const getErrorMessage = (error: any) => {
    if (isDeadLink(error)) {
      return "This password reset link is invalid or has expired. Reset links only work for a few days and can only be used once."
    }
    return getAuthErrorMessage(error, ["new_password1", "new_password2"])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await mutation.mutateAsync({ uid, token, newPassword1, newPassword2 })
      setIsSuccess(true)
    } catch (error) {
      // handled via UI state
    }
  }

  if (isSuccess) {
    return (
      <>
        <Box pb={8}>
          <Link to="/">
            <Image w={{ base: 150, md: 200 }} src="/greenflip_logo.png" alt="GreenFlip" />
          </Link>
        </Box>
        <Box
          bg="gray.100"
          px={{ base: 6, md: 8, lg: 12 }}
          py={{ base: 6, md: 8, lg: 10 }}
          borderRadius="3xl"
          w={{ base: "95%", md: "85%", lg: "80%" }}
          maxW={{ base: "500px", lg: "500px" }}
        >
          <Heading pb={8} size={{ base: "lg", md: "xl" }}>
            Password updated
          </Heading>
          <Text pb={6} color="gray.700" textAlign="left">
            Your password has been reset successfully.
          </Text>
          <Button onClick={() => navigate("/login")} bg="green.800" color="white" _hover={{ bg: "green.900" }}>
            Go to sign in
          </Button>
        </Box>
      </>
    )
  }

  return (
    <>
      <Box pb={8}>
        <Link to="/">
          <Image w={{ base: 150, md: 200 }} src="/greenflip_logo.png" alt="GreenFlip" />
        </Link>
      </Box>

      <Box
        bg="gray.100"
        px={{ base: 6, md: 8, lg: 12 }}
        py={{ base: 6, md: 8, lg: 10 }}
        borderRadius="3xl"
        w={{ base: "95%", md: "85%", lg: "80%" }}
        maxW={{ base: "500px", lg: "500px" }}
      >
        <Heading pb={8} size={{ base: "lg", md: "xl" }}>
          Set a new password
        </Heading>
        <form onSubmit={handleSubmit}>
          <VStack gap={5} align="stretch">
            {mutation.error && (
              <Box
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                color="red.800"
                px={4}
                py={3}
                borderRadius="md"
                fontSize="sm"
              >
                <Text>{getErrorMessage(mutation.error)}</Text>
                {isDeadLink(mutation.error) && (
                  <Text mt={2} fontWeight="bold">
                    <Link to="/reset-password">Request a new reset link</Link>
                  </Text>
                )}
              </Box>
            )}
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                New password
              </Field.Label>
              <Input
                name="new_password1"
                type="password"
                placeholder="Enter new password"
                bg="white"
                value={newPassword1}
                onChange={(e) => setNewPassword1(e.target.value)}
                required
              />
            </Field.Root>
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Confirm new password
              </Field.Label>
              <Input
                name="new_password2"
                type="password"
                placeholder="Re-enter new password"
                bg="white"
                value={newPassword2}
                onChange={(e) => setNewPassword2(e.target.value)}
                required
              />
            </Field.Root>

            <Button
              type="submit"
              bg="green.800"
              color="white"
              _hover={{ bg: "green.900" }}
              size="lg"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving..." : "Reset password"}
            </Button>
          </VStack>
        </form>
      </Box>
      <Text textAlign="center" pt={8}>
        Remember your password? {""}
        <Text display="inline" color="green.800" fontWeight="bold">
          <Link to="/login">Sign in</Link>
        </Text>
      </Text>
    </>
  )
}

export default ResetPasswordConfirm


